<template>
  <div class="export-modal">
    <chip-tag class="export-modal__chiptag" text="Back to editor" @click="closeVideoExport">
      <back-icon />
    </chip-tag>
    <div class="export-modal--in-progress" v-show="exportStore.exportStatus === ExportStatus.EXPORTING">
      <div class="export-modal__message-carousel-container">
        <message-carousel
          class="export-modal__message-carousel"
          :trigger-reset-timer="isModalOpen"
        />
      </div>
      <export-progress-loader
        class="export-modal__export-progress-loader"
      />
    </div>
    <transition name="export-modal__fade-transition">
      <export-complete
        v-show="exportStore.exportStatus === ExportStatus.COMPLETE"
        @close-overlay="closeVideoExport"
      />
    </transition>
    <cancel-export-overlay
      v-show="showCancelExportOverlay && exportStore.exportStatus != ExportStatus.COMPLETE"
      @confirm="closeVideoExport"
      @cancel="showCancelExportOverlay = false"
    />
  </div>
</template>
<script setup lang="ts">
import MessageCarousel from '@/components/export/MessageCarousel.vue'
import ExportProgressLoader from '@/components/export/ExportProgressLoader.vue'
import ExportComplete from '@/components/export/ExportComplete.vue'
import CancelExportOverlay from '@/components/export/CancelExportOverlay.vue'
import ChipTag from '@/components/gallery/ChipTag.vue'
import BackIcon from '@/assets/BackIcon.svg'
import { useExportStore, ExportStatus } from '@/store/export-store'
import { ref, watch } from 'vue'
import { CanvasRecorder } from '@/pixi/canvas-recorder'
const exportStore = useExportStore()

const props = defineProps<{ 
  isModalOpen: boolean,
  canvasRecorder?: CanvasRecorder | null
}>()

watch(() => exportStore.exportStatus, (exportStatus) => {
  if (exportStatus === ExportStatus.ERROR){ 
    closeVideoExport()
  }
})

const emits = defineEmits<{
  (event: 'closeVideoExport'): void
}>()

const showCancelExportOverlay = ref(false)

const closeVideoExport = () => {
  if (showCancelExportOverlay.value
    || exportStore.exportStatus === ExportStatus.COMPLETE
    || exportStore.exportStatus === ExportStatus.ERROR) {
    showCancelExportOverlay.value = false
    props.canvasRecorder?.cancelRecordCanvas()
    emits('closeVideoExport')
    // Use a timeout here to wait for the close animation before reverting back to the export in progress screen.
    exportStore.exportStatus = ExportStatus.INACTIVE
  } else {
    showCancelExportOverlay.value = true
  }
}
</script>

<style lang="scss">
.export-modal {
  height: 100%;
  width: 100%;
  border-radius: 20px 20px 0px 0px;
  overflow: hidden;
  background: linear-gradient(180deg, $purple 0%, $black 80.73%);

  &--in-progress {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  
  &__message-carousel-container {
    flex-grow: 1;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__message-carousel {
    width: 900px;
    height: 350px;
    margin-top: 6px;
    @media (min-width: 1399px) {
      width: 1000px;
    }
  }

  &__progress-bar {
    height: 136px;
    width: 100%;
  }

  &__fade-transition {
    &-enter-active,
    &-leave-active {
      transition: visibility 400ms;
    }

    &-enter-from,
    &-leave-to {
      visibility: hidden;
    }
  }

  &__chiptag {
    position: absolute;
    left: 12px;
    top: 12px;
    z-index: 1;
    .chip-tag--icon {
      border: 1px solid $grey-60;
      &:hover {
        border: 1px solid $white;
      }
    }
  }
}
</style>
